import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Form } from 'antd';

import { CompleteShift, IShift, IShiftCancel, ShiftAction, ShiftStatus, DeleteShiftType } from 'models';
import { API_DATE_FORMAT } from 'utils/date';
import { Api } from '../../../services/api';
import { Ammend, Cancel, Complete, Create, Delete, Get, GetAll, Update } from 'services/shift.service';
import { mapExpense } from '../expenses/hooks';

export interface ShiftDefaultFilters {
  status?: ShiftStatus;
  assignee?: string;
  contact?: string;
  before?: moment.Moment;
  after?: moment.Moment;
}

export const defaultShiftsFilters: ShiftDefaultFilters = {
  status: ShiftStatus.All,
  assignee: '-1',
  contact: '-1',
  before: undefined,
  after: undefined,
};

interface ShiftsProps {
  hasUsersDataAllScope: boolean;
  defaultFilters?: ShiftDefaultFilters;
  pageNumber?: number;
  pageSize?: number;
  status?: ShiftStatus;
  assignee?: string;
  contact?: string;
  before?: moment.Moment;
  after?: moment.Moment;

  onLoad?: () => void;
}

export function useShiftsToInvoice() {
  const [loading, setLoading] = useState<boolean>();
  const [error, setError] = useState<string>();
  const [data, setData] = useState<IShift[]>();

  const load = async (contact: string, assignee: string) => {
    setLoading(true);
    try {
      const rs = await GetAll({ contact, assignee, action: ShiftAction.Invoice });
      setLoading(false);
      setData(rs.data.map(mapShift));
    } catch (err) {
      setLoading(false);
      setError(err as any);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setData([]);
  };

  return { loading, data, error, load, reset };
}

export function mapShift(obj: IShift) {
  return {
    ...obj,
    startDate: moment(obj.startDate),
    endDate: moment(obj.endDate),
    expenses: (obj.expenses || []).map(mapExpense),
  };
}

export function useShifts(props: ShiftsProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<IShift[]>();
  const [hasMoreAfter, setHasMoreAfter] = useState<string | undefined>();
  const [hasMoreBefore, setHasMoreBefore] = useState<string | undefined>();
  const [filterCounter, setFilterCounter] = useState(0);
  const [filter, setFilter] = useState<ShiftDefaultFilters>({
    status: props.defaultFilters?.status,
    assignee: props.defaultFilters?.assignee,
    contact: props.defaultFilters?.contact,
    before: props.defaultFilters?.before,
    after: props.defaultFilters?.after,
  });

  const loadMoreBefore = async (before: string) => {
    const assignee = props.assignee ?? filter.assignee ?? props.defaultFilters?.assignee;
    const contact = props.contact ?? filter.contact;

    const filters: Record<string, unknown> = {
      status: props.status ?? filter.status,
      ...(assignee ? { assignee } : {}),
      ...(contact ? { contact } : {}),
      ...(before ? { before } : {}),
    };

    if (filters.status === ShiftStatus.All) {
      delete filters.status;
    }

    if (filters.status === ShiftStatus.NotInvoiced) {
      delete filters.status;
      filters.invoice = '-1';
    }

    if (filters.contact === '-1') {
      delete filters.contact;
    }

    setLoading(true);
    await Api.tryCatch(
      GetAll(filters),
      (rs) => {
        setLoading(false);
        setData([...rs.data.map(mapShift), ...data!]);
        setHasMoreBefore(rs.hasMoreBefore);
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  };

  const loadMoreAfter = async (after: string) => {
    const assignee = props.assignee ?? filter.assignee ?? props.defaultFilters?.assignee;
    const contact = props.contact ?? filter.contact;

    const filters: Record<string, unknown> = {
      status: props.status ?? filter.status,
      ...(assignee ? { assignee } : {}),
      ...(contact ? { contact } : {}),
      ...(after ? { after } : {}),
    };

    if (filters.status === ShiftStatus.All) {
      delete filters.status;
    }

    if (filters.contact === '-1') {
      delete filters.contact;
    }

    setLoading(true);
    await Api.tryCatch(
      GetAll(filters),
      (rs) => {
        setLoading(false);
        setData([...data!, ...rs.data.map(mapShift)]);
        setHasMoreAfter(rs.hasMoreAfter);
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  };

  const load = async () => {
    const before = props.before ?? filter.before?.format(API_DATE_FORMAT);
    const after = props.after ?? filter.after?.format(API_DATE_FORMAT);
    const assignee = props.assignee ?? filter.assignee ?? props.defaultFilters?.assignee;
    const contact = props.contact ?? filter.contact;

    const filters: Record<string, unknown> = {
      status: props.status ?? filter.status,
      ...(assignee ? { assignee } : {}),
      ...(contact ? { contact } : {}),
      ...(before ? { before } : {}),
      ...(after ? { after } : {}),
    };

    const diffStatus = !_.isEqual(defaultShiftsFilters.status, filters.status);
    const diffAssignee = !_.isEqual(defaultShiftsFilters.assignee, filters.assignee);
    const diffContact = !_.isEqual(defaultShiftsFilters.contact, filters.contact);

    const counter = [diffStatus, !props.hasUsersDataAllScope ? false : diffAssignee, diffContact].filter(Boolean).length;

    setFilterCounter(counter);

    if (!filters.before && !filters.after) {
      filters.before = moment().add(7, 'days').format(API_DATE_FORMAT);
    }

    // Filter all should not be passed
    if (filters.status === ShiftStatus.All) {
      delete filters.status;
    }
    if (filters.status === ShiftStatus.NotInvoiced) {
      filters.status = ShiftStatus.Completed;
      filters.invoice = -1;
    }
    if (filters.contact === '-1') {
      delete filters.contact;
    }

    setLoading(true);
    await Api.tryCatch(
      GetAll(filters),
      (rs) => {
        setLoading(false);
        setData(rs.data.map(mapShift));
        setHasMoreAfter(rs.hasMoreAfter);
        setHasMoreBefore(rs.hasMoreBefore);

        props.onLoad?.();
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  };

  let cancelToken = Api.getCancelTokenSource();
  useEffect(() => {
    cancelToken = Api.getCancelTokenSource();
    return () => {
      cancelToken.cancel();
    };
  }, []);

  useEffect(() => {
    load();
  }, [props.pageNumber, props.pageSize, props.status, props.assignee, props.contact, props.before, props.after, filter]);

  return { loading, error, data, filterCounter, setFilter, reloadList: load, hasMoreAfter, hasMoreBefore, loadMoreAfter, loadMoreBefore };
}

export function useRemoveShift() {
  const [isDeleting, setIsDeleting] = useState(false);
  const [form] = Form.useForm();

  const remove = async (id: string, type: DeleteShiftType) => {
    setIsDeleting(true);
    try {
      await Delete(id, type);
    } finally {
      setIsDeleting(false);
    }
  };

  return { isDeleting, remove, form };
}

export function useShift() {
  const [loading, setIsLoading] = useState(false);

  const load = async (id: string) => {
    setIsLoading(true);
    try {
      const obj = await Get(id);
      return mapShift(obj);
    } finally {
      setIsLoading(false);
    }
  };

  return { loading, load };
}

export function useSaveShift() {
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const save = async (payload: IShift) => {
    setIsSaving(true);
    try {
      if (payload.id) {
        return await Update(payload.id, payload);
      }
      return await Create(payload);
    } finally {
      setIsSaving(false);
    }
  };

  const complete = async (payload: CompleteShift) => {
    setIsSaving(true);
    try {
      return await Complete(payload);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, save, complete, form };
}

export function useAmendShift() {
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const amend = async (id: string) => {
    setIsSaving(true);
    try {
      return await Ammend(id);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, amend, form };
}

export function useCancelShift() {
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const cancel = async (payload: IShiftCancel) => {
    setIsSaving(true);
    try {
      return await Cancel(payload);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, cancel, form };
}
